import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Permission, Role } from '@gymos/shared';
import { CROSS_BRANCH_ROLES } from '@gymos/shared';
import {
  api,
  ApiError,
  setAccessToken,
  setActiveBranchId as setApiBranchId,
  setAuthFailureHandler,
} from './api';

// --- Server contract --------------------------------------------------------

export interface AuthBranch {
  id: string;
  code: string;
  name: string;
  isPrimary?: boolean;
}

export interface AuthUser {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: Role;
  memberId: string | null;
  avatarUrl: string | null;
  designation?: string | null;
  mustResetPassword: boolean;
  branches: AuthBranch[];
}

/** Returned by /auth/login, /auth/refresh and /auth/member/verify-otp alike. */
export interface AuthSessionPayload {
  accessToken: string;
  user: AuthUser;
  permissions: Permission[];
}

export interface OtpChallenge {
  ok: boolean;
  message: string;
  expiresInSeconds: number;
  /** Present only when the API runs in dev — lets you sign in before WhatsApp is paired. */
  devCode?: string;
}

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  permissions: Permission[];
  branches: AuthBranch[];
  /** null means "all branches" — only meaningful for ADMIN / SUPER_ADMIN. */
  activeBranchId: string | null;
  activeBranch: AuthBranch | null;
  /** True when the role may read across branches without an assignment. */
  canSwitchToAllBranches: boolean;
  isStaff: boolean;
  isMember: boolean;

  setActiveBranch: (branchId: string | null) => void;
  login: (identifier: string, password: string) => Promise<AuthUser>;
  memberRequestOtp: (phone: string) => Promise<OtpChallenge>;
  memberVerifyOtp: (phone: string, code: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  /** Re-reads /auth/me — call after a profile or role change. */
  reloadSession: () => Promise<void>;

  hasPermission: (permission: Permission) => boolean;
  hasAny: (...permissions: Permission[]) => boolean;
  hasAll: (...permissions: Permission[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const BRANCH_STORAGE_KEY = 'gymos.branchId';

/**
 * Only the *selected branch* is persisted. Tokens deliberately are not:
 * the access token lives in memory (see lib/api.ts) and the refresh token is an
 * httpOnly cookie that JavaScript cannot read.
 */
function readStoredBranchId(): string | null {
  try {
    return localStorage.getItem(BRANCH_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredBranchId(branchId: string | null): void {
  try {
    if (branchId) localStorage.setItem(BRANCH_STORAGE_KEY, branchId);
    else localStorage.removeItem(BRANCH_STORAGE_KEY);
  } catch {
    /* private mode */
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [branches, setBranches] = useState<AuthBranch[]>([]);
  const [activeBranchId, setActiveBranchIdState] = useState<string | null>(readStoredBranchId);

  // Keeps the api module's header in sync without a re-render loop.
  useEffect(() => {
    setApiBranchId(activeBranchId);
  }, [activeBranchId]);

  const permissionSet = useMemo(() => new Set<string>(permissions), [permissions]);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setPermissions([]);
    setBranches([]);
    setStatus('anonymous');
  }, []);

  const applySession = useCallback((payload: AuthSessionPayload) => {
    setAccessToken(payload.accessToken);
    setUser(payload.user);
    setPermissions(payload.permissions ?? []);
    setBranches(payload.user.branches ?? []);
    setStatus('authenticated');

    // Reconcile the remembered branch against what this account can actually see.
    const assigned = payload.user.branches ?? [];
    const crossBranch = CROSS_BRANCH_ROLES.includes(payload.user.role);
    const remembered = readStoredBranchId();

    let next: string | null = null;
    if (remembered && assigned.some((b) => b.id === remembered)) {
      next = remembered;
    } else if (assigned.length > 0 && !crossBranch) {
      next = (assigned.find((b) => b.isPrimary) ?? assigned[0]).id;
    } else if (assigned.length === 1) {
      next = assigned[0].id;
    }

    setActiveBranchIdState(next);
    setApiBranchId(next);
    writeStoredBranchId(next);
    return payload.user;
  }, []);

  // A failed refresh anywhere in the app lands here. State is cleared and the
  // route guards do the redirecting, so no hard page reload is needed.
  useEffect(() => {
    setAuthFailureHandler(() => clearSession());
    return () => setAuthFailureHandler(() => {});
  }, [clearSession]);

  // Restore the session on mount. The page reload lost the in-memory access
  // token, but the httpOnly refresh cookie survived — trade it for a new one.
  const bootstrapped = useRef(false);
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    // Deliberately no `cancelled` flag here.
    //
    // Under StrictMode the effect runs, is torn down, and runs again. The ref
    // guard above makes the second run a no-op — so a cleanup that cancelled the
    // *first* run would suppress the only in-flight refresh there will ever be,
    // and the app would sit on "Loading…" forever with a perfectly good session.
    //
    // Refreshing twice is worse than a late state update: rotation revokes the
    // presented token, so a second concurrent refresh looks like token theft to
    // the API and kills every session for that user.
    void (async () => {
      try {
        const payload = await api.post<AuthSessionPayload>('/auth/refresh', undefined, {
          skipAuthRefresh: true,
          omitBranch: true,
        });
        applySession(payload);
      } catch {
        // No cookie, expired cookie, or API not running yet — all mean "signed out".
        setAccessToken(null);
        setStatus('anonymous');
      }
    })();
  }, [applySession]);

  const login = useCallback(
    async (identifier: string, password: string) => {
      const payload = await api.post<AuthSessionPayload>(
        '/auth/login',
        { identifier, password },
        { skipAuthRefresh: true, omitBranch: true },
      );
      return applySession(payload);
    },
    [applySession],
  );

  const memberRequestOtp = useCallback(async (phone: string) => {
    return api.post<OtpChallenge>(
      '/auth/member/request-otp',
      { phone },
      { skipAuthRefresh: true, omitBranch: true },
    );
  }, []);

  const memberVerifyOtp = useCallback(
    async (phone: string, code: string) => {
      const payload = await api.post<AuthSessionPayload>(
        '/auth/member/verify-otp',
        { phone, code },
        { skipAuthRefresh: true, omitBranch: true },
      );
      return applySession(payload);
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', undefined, { skipAuthRefresh: true, omitBranch: true });
    } catch {
      // Server-side revocation is best-effort; the local session goes either way.
    }
    clearSession();
  }, [clearSession]);

  const reloadSession = useCallback(async () => {
    try {
      const { user: nextUser, permissions: nextPermissions } = await api.get<{
        user: AuthUser;
        permissions: Permission[];
      }>('/auth/me');
      setUser(nextUser);
      setPermissions(nextPermissions ?? []);
      setBranches(nextUser.branches ?? []);
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthorized) clearSession();
      else throw err;
    }
  }, [clearSession]);

  const setActiveBranch = useCallback((branchId: string | null) => {
    setActiveBranchIdState(branchId);
    setApiBranchId(branchId);
    writeStoredBranchId(branchId);
  }, []);

  const hasPermission = useCallback(
    (permission: Permission) => permissionSet.has(permission),
    [permissionSet],
  );
  const hasAny = useCallback(
    (...list: Permission[]) => list.some((p) => permissionSet.has(p)),
    [permissionSet],
  );
  const hasAll = useCallback(
    (...list: Permission[]) => list.every((p) => permissionSet.has(p)),
    [permissionSet],
  );

  const value = useMemo<AuthContextValue>(() => {
    const canSwitchToAllBranches = user ? CROSS_BRANCH_ROLES.includes(user.role) : false;
    return {
      status,
      user,
      permissions,
      branches,
      activeBranchId,
      activeBranch: branches.find((b) => b.id === activeBranchId) ?? null,
      canSwitchToAllBranches,
      isStaff: !!user && user.role !== 'MEMBER',
      isMember: user?.role === 'MEMBER',
      setActiveBranch,
      login,
      memberRequestOtp,
      memberVerifyOtp,
      logout,
      reloadSession,
      hasPermission,
      hasAny,
      hasAll,
    };
  }, [
    status,
    user,
    permissions,
    branches,
    activeBranchId,
    setActiveBranch,
    login,
    memberRequestOtp,
    memberVerifyOtp,
    logout,
    reloadSession,
    hasPermission,
    hasAny,
    hasAll,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Convenience for conditionally rendering an action the user may not have. */
export function usePermission(permission: Permission): boolean {
  return useAuth().hasPermission(permission);
}
